import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/Button";
import { Theme } from "@/components/ui/context/theme";
import { useConfig as useMoonshineConfig } from "@/components/ui/hooks/useConfig";
import { Check, Copy } from "lucide-react";
import React, { useEffect } from "react";
import {
  BuiltinTheme,
  type BundledLanguage,
  codeToHtml,
  codeToTokens,
  type ThemedToken,
} from "shiki";

const DEFAULT_THEME_PER_MODE: Record<Theme, BuiltinTheme> = {
  light: "github-light-default",
  dark: "github-dark-default",
};

/**
 * A slot lets the caller host an interactive React node inline inside the
 * highlighted code, in place of a sentinel string. Keyed by the exact token
 * text shiki produces for the sentinel — for a JSON value that's the quoted
 * form, e.g. `"\"__SLOT_orgToken__\""`. Used opt-in: passing `slots` switches
 * CodeBlock to a token-rendered path (real React nodes) instead of the default
 * dangerouslySetInnerHTML path, so existing usages are unaffected.
 */
export type CodeBlockSlot = {
  /** React node rendered inline where the sentinel token was. */
  node: React.ReactNode;
  /** Text substituted for the sentinel when copying, so the clipboard stays
   *  valid while the slot is unfilled. Defaults to "". */
  copyText?: string;
};

// shiki FontStyle is a bitmask: Italic=1, Bold=2, Underline=4 (None=0/-1).
function fontStyleProps(fs?: number): React.CSSProperties {
  if (!fs || fs < 0) return {};
  return {
    fontStyle: fs & 1 ? "italic" : undefined,
    fontWeight: fs & 2 ? "bold" : undefined,
    textDecoration: fs & 4 ? "underline" : undefined,
  };
}

export function CodeBlock({
  children: code,
  language,
  className,
  innerClassName,
  copyable = true,
  onCopy,
  preClassName,
  slots,
  copyLabel = "code",
}: {
  children: string;
  language?: string;
  className?: string;
  innerClassName?: string;
  copyable?: boolean;
  onCopy?: () => void;
  preClassName?: string;
  slots?: Record<string, CodeBlockSlot>;
  copyLabel?: string;
}): React.JSX.Element {
  const { theme } = useMoonshineConfig();
  const hasSlots = !!slots && Object.keys(slots).length > 0;
  const [highlightedCode, setHighlightedCode] = React.useState<string | null>(
    null,
  );
  const [tokenResult, setTokenResult] = React.useState<Awaited<
    ReturnType<typeof codeToTokens>
  > | null>(null);
  const [copied, setCopied] = React.useState(false);

  // What the copy button actually writes: with slots, substitute each sentinel
  // with its copyText so an unfilled inline action never leaks the sentinel.
  const copyText = React.useMemo(() => {
    if (!slots) return code;
    let out = code;
    for (const [sentinel, slot] of Object.entries(slots)) {
      out = out.split(sentinel).join(slot.copyText ?? "");
    }
    return out;
  }, [code, slots]);

  const handleCopy = () => {
    void navigator.clipboard.writeText(copyText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    onCopy?.();
  };

  useEffect(() => {
    if (!language) return;
    let cancelled = false;

    if (hasSlots) {
      // codeToTokens types `lang` stricter than codeToHtml's loose string; the
      // prop is a free-form string, so narrow it the same way callers expect.
      void codeToTokens(code, {
        lang: language as BundledLanguage,
        theme: DEFAULT_THEME_PER_MODE[theme],
      }).then((res) => {
        if (!cancelled) setTokenResult(res);
      });
    } else {
      void codeToHtml(code, {
        lang: language,
        theme: DEFAULT_THEME_PER_MODE[theme],
        transformers: [
          {
            pre(node) {
              // the github shiki themes come with a pre-defined background color, we don't want that
              node.properties.class = cn(
                "!bg-transparent",
                preClassName,
                theme === "dark" ? "dark" : "light",
              );
            },
          },
        ],
      }).then((html) => {
        if (!cancelled) setHighlightedCode(html);
      });
    }

    return () => {
      cancelled = true;
    };
  }, [code, language, preClassName, theme, hasSlots]);

  // shiki's raw <pre> output carries the browser's UA-stylesheet
  // `white-space: pre` — a rule matching the element itself always beats an
  // inherited value from an ancestor, so whitespace-pre-wrap on this wrapper
  // alone never reaches it. Target the injected <pre> directly.
  const baseClasses =
    "font-mono text-sm text-wrap overflow-x-auto border break-all whitespace-pre-wrap [&>pre]:whitespace-pre-wrap [&>pre]:break-all";
  const innerClasses = cn(baseClasses, "p-4 pr-12", innerClassName);
  // Shown until the async highlight resolves (and as the slot-path placeholder).
  const fallback = <div className={innerClasses}>{code}</div>;

  // Token-rendered path: shiki tokens as real React nodes so a slot can host an
  // interactive element inline. Mirrors shiki's <pre><code> structure so the
  // visual matches the default path.
  const renderTokens = (result: Awaited<ReturnType<typeof codeToTokens>>) => {
    const lines = result.tokens as ThemedToken[][];
    return (
      <pre
        className={cn(
          "!bg-transparent break-all whitespace-pre-wrap",
          preClassName,
        )}
        style={{ color: result.fg }}
      >
        <code>
          {lines.map((line, li) => (
            <React.Fragment key={li}>
              {line.map((tok, ti) => {
                // Match by substring rather than exact token text: the caller
                // keys slots by a plain sentinel (e.g. "__SLOT_orgToken__") and
                // shiki may wrap it (quotes, etc.) in the token it emits, so the
                // page doesn't have to know shiki's exact tokenization.
                const slotKey =
                  slots &&
                  Object.keys(slots).find((k) => tok.content.includes(k));
                if (slotKey) {
                  return (
                    <React.Fragment key={ti}>
                      {slots[slotKey]!.node}
                    </React.Fragment>
                  );
                }
                return (
                  <span
                    key={ti}
                    style={{
                      color: tok.color,
                      ...fontStyleProps(tok.fontStyle),
                    }}
                  >
                    {tok.content}
                  </span>
                );
              })}
              {li < lines.length - 1 ? "\n" : null}
            </React.Fragment>
          ))}
        </code>
      </pre>
    );
  };

  return (
    <div className={cn("group relative", className)}>
      {hasSlots ? (
        tokenResult ? (
          <div className={innerClasses}>{renderTokens(tokenResult)}</div>
        ) : (
          fallback
        )
      ) : highlightedCode ? (
        <div
          className={innerClasses}
          dangerouslySetInnerHTML={{ __html: highlightedCode ?? "" }}
        />
      ) : (
        fallback
      )}
      {copyable && (
        <Button
          variant="tertiary"
          size="sm"
          onClick={handleCopy}
          className="absolute top-2 right-2 p-2"
        >
          <Button.LeftIcon>
            {copied ? (
              <Check className="h-4 w-4" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </Button.LeftIcon>
          <Button.Text className="sr-only">
            {copied ? `Copied ${copyLabel}` : `Copy ${copyLabel}`}
          </Button.Text>
        </Button>
      )}
    </div>
  );
}
